import { IUser } from 'src/interfaces/user';
import { ILead } from 'src/interfaces/lead';
import { LEAD_AUTH, READ_PERMISSION } from 'src/constants/permissions';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    ADMIN_LEVEL_ACCESS,
    DEVELOPER_LEVEL_ACCESS,
    GROUP_LEVEL_ACCESS,
    USER_LEVEL_ACCESS,
} from 'src/components/access';

/**
 * Lead fields the access rule depends on.
 */
type TLeadAccessTarget = Pick<Partial<ILead>, 'store'> & {
    agent?: Pick<IUser, '_id'>;
};

/**
 * Encapsulates lead-access rule:
 * Must have LEAD read permission.
 * Developer: always can access.
 * Admin: only leads in assigned stores.
 * Group: only leads in group stores.
 * User: only leads assigned to them (lead.agent).
 */
export const canAccessLeadDetail = ({
    user,
    lead,
}: {
    user: IUser;
    lead: TLeadAccessTarget;
}): boolean => {
    if (!user) {
        return false;
    }

    const hasLeadReadPermission = isPermissionGranted({
        permissions: user.permissions || ({} as any),
        permission: LEAD_AUTH,
        crud: READ_PERMISSION,
    });

    if (!hasLeadReadPermission) {
        return false;
    }

    const leadStoreId =
        typeof lead?.store === 'string' ? lead.store : lead?.store?._id;

    if (user.accessLevel === DEVELOPER_LEVEL_ACCESS) {
        return true;
    }

    if (user.accessLevel === ADMIN_LEVEL_ACCESS) {
        if (!leadStoreId) {
            return false;
        }
        return (user.stores || []).some((store) => {
            const storeId = typeof store === 'string' ? store : store?._id;
            return storeId === leadStoreId;
        });
    }

    if (user.accessLevel === GROUP_LEVEL_ACCESS) {
        if (!leadStoreId) {
            return false;
        }
        return (user.group?.stores || []).some((store) => {
            const storeId = typeof store === 'string' ? store : store?._id;
            return storeId === leadStoreId;
        });
    }

    if (user.accessLevel === USER_LEVEL_ACCESS) {
        return lead?.agent?._id === user._id;
    }

    return false;
};
